import ky from 'ky';

import { API } from './config.ts';
import {
  getCurrentTokensOperation,
  getTokens,
  isTokenExpired,
  obtainNewTokens,
  refreshTokens,
  type Tokens,
} from './tokens/index.ts';
export { HTTPError } from 'ky';

export class FetchError extends Error {
  name = 'FetchError';
}

// Convenient wrapper around fetch, makes it easy to know when network errors occur
export const fetchWithError = async (input: string | URL | Request, init?: RequestInit) => {
  try {
    return await fetch(input, init);
  } catch (e) {
    throw new FetchError('Fetch error', { cause: e });
  }
};

export type ApiClientConfig = {
  canObtainNewTokens: (
    requestUrl: string | undefined,
    requestBody: Record<string, any> | undefined,
  ) => boolean;
  onLogout: () => void;
};

let config: ApiClientConfig | undefined = undefined;

export const initializeApiClient = (apiConfig: ApiClientConfig) => {
  config = apiConfig;
};

export const apiClient = ky.create({
  fetch: fetchWithError,
  prefixUrl: API.url,
  retry: {
    retryOnTimeout: true,
  },
  credentials: 'include',
  hooks: {
    beforeRequest: [
      () => {
        if (!config) {
          throw new Error('Api client is not initialized, call initializeApiClient() first.');
        }
      },
      async (request) => {
        await getCurrentTokensOperation();
        const token = getTokens();
        if (token) {
          request.headers.set('authorization', `Bearer ${token.accessToken}`);
        }
      },
    ],
    afterResponse: [
      async (request, options, response) => {
        if (response.status === 401) {
          const ignoreError = <T>(promise: Promise<T>) => promise.catch(() => undefined);

          if (canRefreshTokens(getTokens()) && (await ignoreError(refreshTokens()))) {
            return ky.retry({ delay: 0 });
          }

          if (
            config!.canObtainNewTokens(request.url, await ignoreError(request.clone().json())) &&
            (await ignoreError(obtainNewTokens()))
          ) {
            return ky.retry({ delay: 0 });
          }

          config!.onLogout();
        }
      },
    ],
  },
});

const canRefreshTokens = (tokens: Tokens | null | undefined): tokens is Tokens => {
  if (tokens?.refreshToken) {
    return !isTokenExpired(tokens.refreshToken);
  }

  return false;
};
